import { Modal, Form, Input, Select, Radio, Checkbox, Rate, Upload, Button, DatePicker, TimePicker, Slider, Switch, InputNumber, Row, Col, Typography } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import { FormField as FormFieldType } from './types';

const { Title, Text } = Typography;
const { TextArea } = Input;

interface FormPreviewProps {
  visible: boolean;
  form: {
    name: string;
    description?: string;
    layout: {
      columns: number;
      fields: FormFieldType[];
    };
  } | null;
  onClose: () => void;
}

export default function FormPreview({ visible, form, onClose }: FormPreviewProps) {
  if (!form) return null;

  const renderField = (field: FormFieldType) => {
    const isRequired = field.required;
    const label = (
      <span>
        {field.label} {isRequired && <span style={{ color: 'red' }}>*</span>}
      </span>
    );

    switch (field.type) {
      case 'text':
      case 'email':
      case 'phone':
      case 'url':
        return (
          <Form.Item label={label} required={isRequired}>
            <Input placeholder={field.placeholder} />
          </Form.Item>
        );

      case 'textarea':
        return (
          <Form.Item label={label} required={isRequired}>
            <TextArea rows={4} placeholder={field.placeholder} />
          </Form.Item>
        );

      case 'password':
        return (
          <Form.Item label={label} required={isRequired}>
            <Input.Password placeholder={field.placeholder} />
          </Form.Item>
        );

      case 'number':
      case 'decimal':
        return (
          <Form.Item label={label} required={isRequired}>
            <InputNumber style={{ width: '100%' }} placeholder={field.placeholder} />
          </Form.Item>
        );

      case 'currency':
        return (
          <Form.Item label={label} required={isRequired}>
            <InputNumber
              style={{ width: '100%' }}
              prefix="$"
              placeholder={field.placeholder}
            />
          </Form.Item>
        );

      case 'date':
        return (
          <Form.Item label={label} required={isRequired}>
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
        );

      case 'time':
        return (
          <Form.Item label={label} required={isRequired}>
            <TimePicker style={{ width: '100%' }} />
          </Form.Item>
        );

      case 'datetime':
        return (
          <Form.Item label={label} required={isRequired}>
            <DatePicker showTime style={{ width: '100%' }} />
          </Form.Item>
        );

      case 'select':
        return (
          <Form.Item label={label} required={isRequired}>
            <Select placeholder={field.placeholder || 'Select an option'}>
              {field.options?.map((opt, idx) => (
                <Select.Option key={idx} value={opt}>
                  {opt}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        );

      case 'multiselect':
        return (
          <Form.Item label={label} required={isRequired}>
            <Select mode="multiple" placeholder={field.placeholder || 'Select options'}>
              {field.options?.map((opt, idx) => (
                <Select.Option key={idx} value={opt}>
                  {opt}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        );

      case 'radio':
        return (
          <Form.Item label={label} required={isRequired}>
            <Radio.Group>
              {field.options?.map((opt, idx) => (
                <Radio key={idx} value={opt}>
                  {opt}
                </Radio>
              ))}
            </Radio.Group>
          </Form.Item>
        );

      case 'checkbox':
        return (
          <Form.Item label={label} required={isRequired}>
            <Checkbox.Group options={field.options} />
          </Form.Item>
        );

      case 'switch':
        return (
          <Form.Item label={label} required={isRequired} valuePropName="checked">
            <Switch />
          </Form.Item>
        );

      case 'rating':
        return (
          <Form.Item label={label} required={isRequired}>
            <Rate />
          </Form.Item>
        );

      case 'slider':
        return (
          <Form.Item label={label} required={isRequired}>
            <Slider />
          </Form.Item>
        );

      case 'file':
      case 'image':
        return (
          <Form.Item label={label} required={isRequired}>
            <Upload>
              <Button icon={<UploadOutlined />}>Click to Upload</Button>
            </Upload>
          </Form.Item>
        );

      case 'heading':
        return (
          <div style={{ marginBottom: 16 }}>
            <Title level={4}>{field.label}</Title>
          </div>
        );

      case 'divider':
        return <div style={{ borderTop: '1px solid #e8e8e8', margin: '16px 0' }} />;

      default:
        return (
          <Form.Item label={label} required={isRequired}>
            <Input placeholder={field.placeholder} />
          </Form.Item>
        );
    }
  };

  // Organize fields into rows based on grid layout
  const renderFormGrid = () => {
    const rows: FormFieldType[][] = [];
    const rowMap = new Map<number, FormFieldType[]>();

    form.layout.fields.forEach((field) => {
      const row = field.position.row;
      if (!rowMap.has(row)) rowMap.set(row, []);
      rowMap.get(row)!.push(field);
    });

    const sortedRows = Array.from(rowMap.keys()).sort((a, b) => a - b);
    sortedRows.forEach((rowNum) => {
      rows.push(rowMap.get(rowNum)!.sort((a, b) => a.position.col - b.position.col));
    });

    return rows.map((row, rowIndex) => (
      <Row gutter={16} key={rowIndex}>
        {row.map((field) => {
          const span = ((field.position.colSpan || 1) / form.layout.columns) * 24;
          return (
            <Col span={span} key={field.id}>
              {renderField(field)}
            </Col>
          );
        })}
      </Row>
    ));
  };

  return (
    <Modal
      title="Form Preview"
      open={visible}
      onCancel={onClose}
      width={800}
      footer={[
        <Button key="close" onClick={onClose}>
          Close
        </Button>,
      ]}
    >
      <div style={{ padding: '16px 0' }}>
        <Title level={3}>{form.name}</Title>
        {form.description && (
          <Text type="secondary" style={{ display: 'block', marginBottom: 24 }}>
            {form.description}
          </Text>
        )}

        <Form layout="vertical">
          {renderFormGrid()}
        </Form>
      </div>
    </Modal>
  );
}
